import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Stack,
  Chip,
  Collapse,
  Grid,
  Slider,
  Button,
  Autocomplete,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import BulkActions from './BulkActions';
import Rules from './Rules';

const FilterSection = ({
  filters,
  setFilters,
  allTags,
  minAmount,
  maxAmount,
  minDate,
  maxDate,
  isOpen,
  onBulkAction,
  rules = [],
  onRuleApply,
  onRuleCreate,
  onRuleOrderChange,
  hideActions,
}) => {
  // Store original filter values
  const originalFilters = {
    minAmount: minAmount,
    maxAmount: maxAmount,
    filterMinAmount: minAmount,
    filterMaxAmount: maxAmount,
    minDate: minDate,
    maxDate: maxDate,
    filterStartDate: minDate,
    filterEndDate: maxDate
  };
  // Load filters from localStorage on component mount only if they haven't been loaded yet
  useEffect(() => {
    // Only load from localStorage if current filters are at their initial values
    if (filters.filterMinAmount === -Infinity && filters.filterMaxAmount === Infinity) {
      const savedFilters = localStorage.getItem('tableFilters');
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        // Convert date strings back to Date objects
        if (parsedFilters.minDate) {
          parsedFilters.minDate = new Date(parsedFilters.minDate);
        }
        if (parsedFilters.maxDate) {
          parsedFilters.maxDate = new Date(parsedFilters.maxDate);
        }
        setFilters(parsedFilters);
      }
    }
  }, [setFilters, filters.filterMinAmount, filters.filterMaxAmount]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    // Create a copy of filters to avoid modifying the original state
    const filtersToSave = { ...filters };
    // Convert Date objects to ISO strings for proper serialization
    if (filtersToSave.minDate instanceof Date) {
      filtersToSave.minDate = filtersToSave.minDate.toISOString();
    }
    if (filtersToSave.maxDate instanceof Date) {
      filtersToSave.maxDate = filtersToSave.maxDate.toISOString();
    }
    localStorage.setItem('tableFilters', JSON.stringify(filtersToSave));
  }, [filters]);

  return (
    <Collapse in={isOpen} timeout={200}>
      <Box sx={{ 
        width: '100%', 
        borderRadius: 1,
      }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>

        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2} columns={5}>
            {/* Generic Search */}
            <Grid size={{ xs:5, md:2, lg:1 }}>
              <Stack spacing={2}>
                <TextField
                  label="Search All Fields"
                  size="small"
                  value={filters.searchText}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFilters({
                      searchText: '',
                      tags: [],
                      description: '',
                      description2: '',
                      comments: '',
                      minDate: originalFilters.minDate,
                      maxDate: originalFilters.maxDate,
                      filterStartDate: originalFilters.filterStartDate,
                      filterEndDate: originalFilters.filterEndDate,
                      minAmount: originalFilters.minAmount,
                      maxAmount: originalFilters.maxAmount,
                      filterMinAmount: originalFilters.filterMinAmount,
                      filterMaxAmount: originalFilters.filterMaxAmount,
                      tax: { true: true, false: true },
                      showUntagged: false,
                    });
                    localStorage.removeItem('tableFilters');
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Clear All Filters
                </Button>
              </Stack>
            </Grid>

            {/* Tags Filter */}
            <Grid size={{ xs:5, md:2, lg:1 }}>
              <Box>
                <Box>
                  <Autocomplete
                    multiple
                    size="small"
                    options={[...allTags].sort()}
                    value={filters.tags}
                    onChange={(event, newValue) => {
                      setFilters(prev => ({
                        ...prev,
                        tags: newValue
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Filter by tags..."
                        size="small"
                      />
                    )}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '3px'
                      }
                    }}
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.showUntagged}
                      onChange={(e) => setFilters(prev => ({ ...prev, showUntagged: e.target.checked }))}
                    />
                  }
                  label="Show untagged only"
                />
                <datalist id="filter-tag-suggestions">
                  {[...allTags].sort().map((tag, i) => (
                    <option key={i} value={tag} />
                  ))}
                </datalist>
              </Box>
            </Grid>

            {/* Description and Comments Filters */}
            <Grid size={{ xs:5, md:2, lg:1 }}>
              <Stack spacing={2}>
                <TextField
                  label="Description"
                  size="small"
                  value={filters.description}
                  onChange={(e) => setFilters(prev => ({ ...prev, description: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Description 2"
                  size="small"
                  value={filters.description2}
                  onChange={(e) => setFilters(prev => ({ ...prev, description2: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Comments"
                  size="small"
                  value={filters.comments}
                  onChange={(e) => setFilters(prev => ({ ...prev, comments: e.target.value }))}
                  fullWidth
                />
              </Stack>
            </Grid>

            {/* Date Range and Amount Range */}
            <Grid size={{ xs:5, md:2, lg:1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack spacing={2}>
                  <DatePicker
                    label="Start Date"
                    value={filters.filterStartDate || null}
                    onChange={(newValue) => {
                      // Only update if it's a valid date
                      if (newValue && !isNaN(newValue)) {
                        setFilters(prev => ({
                          ...prev,
                          filterStartDate: newValue
                        }));
                      } else {
                        setFilters(prev => ({
                          ...prev,
                          filterStartDate: null
                        }));
                      }
                    }}
                    format="yyyy-MM-dd"
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: filters.filterStartDate && isNaN(filters.filterStartDate)
                      }
                    }}
                  />
                  <DatePicker
                    label="End Date"
                    value={filters.filterEndDate || null}
                    onChange={(newValue) => {
                      // Only update if it's a valid date
                      if (newValue && !isNaN(newValue)) {
                        setFilters(prev => ({
                          ...prev,
                          filterEndDate: newValue
                        }));
                      } else {
                        setFilters(prev => ({
                          ...prev,
                          filterEndDate: null
                        }));
                      }
                    }}
                    format="yyyy-MM-dd"
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: filters.filterEndDate && isNaN(filters.filterEndDate)
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setFilters(prev => ({
                        ...prev,
                        filterStartDate: minDate,
                        filterEndDate: maxDate
                      }));
                    }}
                    fullWidth
                  >
                    Clear Date Range
                  </Button>
                </Stack>
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs:5, md:2, lg:1 }}>
              <Box sx={{ px: 2, width: '100%' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Amount Range
                </Typography>
                <Slider
                  value={[
                    typeof filters.filterMinAmount === 'number' && !isNaN(filters.filterMinAmount) ? filters.filterMinAmount : 
                    typeof minAmount === 'number' && !isNaN(minAmount) ? minAmount : 0,
                    typeof filters.filterMaxAmount === 'number' && !isNaN(filters.filterMaxAmount) ? filters.filterMaxAmount : 
                    typeof maxAmount === 'number' && !isNaN(maxAmount) ? maxAmount : 100
                  ]}
                  onChange={(_, newValue) => {
                    if (Array.isArray(newValue) && newValue.length === 2) {
                      setFilters(prev => ({
                        ...prev,
                        filterMinAmount: newValue[0],
                        filterMaxAmount: newValue[1]
                      }));
                    }
                  }}
                  valueLabelDisplay="auto"
                  min={typeof minAmount === 'number' && !isNaN(minAmount) ? minAmount : 0}
                  max={typeof maxAmount === 'number' && !isNaN(maxAmount) ? maxAmount : 100}
                  sx={{ 
                    width: '100%',
                    minWidth: '180px',
                    '& .MuiSlider-thumb': {
                      width: 24,
                      height: 24,
                    },
                    '& .MuiSlider-track': {
                      height: 8
                    },
                    '& .MuiSlider-rail': {
                      height: 8
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption">
                    ${filters.filterMinAmount}
                  </Typography>
                  <Typography variant="caption">
                    ${filters.filterMaxAmount}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <Chip
                  label="Tax"
                  onClick={() => {
                    setFilters(prev => ({
                      ...prev,
                      tax: {
                        ...prev.tax,
                        true: !prev.tax.true
                      }
                    }));
                  }}
                  variant={filters.tax.true ? "filled" : "outlined"}
                  color='primary'
                />
                <Chip
                  label="Non-Tax"
                  onClick={() => {
                    setFilters(prev => ({
                      ...prev,
                      tax: {
                        ...prev.tax,
                        false: !prev.tax.false
                      }
                    }));
                  }}
                  variant={filters.tax.false ? "filled" : "outlined"}
                  color='primary'
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
        {!hideActions && (
          <BulkActions
            onBulkAction={onBulkAction}
            allTags={allTags}
          />
        )}

        {!hideActions && (
          <Rules
            rules={rules}
            onRuleApply={onRuleApply}
            onRuleCreate={onRuleCreate}
            onRuleOrderChange={onRuleOrderChange}
            currentFilters={filters}
            allTags={allTags}
            minAmount={minAmount}
            maxAmount={maxAmount}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
      </Box>
    </Collapse>
  );
};

export default FilterSection;


































































